import produce from 'immer';
import { combineReducers } from 'redux';
import { toSafeUIData } from 'vrlcm-gui-utils';
import { RequestDashboardData } from '../../actions/Dashboard';
import { EnvironmentsAction } from '../../actions/Environments';
import { EnvironmentWorkflowAction } from '../../actions/workflows/EnvironmentWorkflow';
import * as Constants from '../../constants/action-constants';
import initialState from '../../InitialState';
import { EnvironmentWorkflow } from '../../types/workflows/EnvironmentWorkflow';
import { generateUniqueId } from '../../utils/Helpers';
import binaryMappingStep from './steps/BinaryMapping';
import environmentStep from './steps/Environment';
import eulaStep from './steps/Eula';
import infrastructureStep from './steps/Infrastructure';
import networkStep from './steps/Network';
import precheckStep from './steps/Precheck';
import productsStep from './steps/Product';
import selectProductStep from './steps/SelectProduct';

type Action =
  | EnvironmentWorkflowAction
  | RequestDashboardData
  | EnvironmentsAction;

const stateReducer = (
  state: EnvironmentWorkflow = initialState.environmentWorkflow,
  action: Action
): EnvironmentWorkflow => {
  return produce<EnvironmentWorkflow, EnvironmentWorkflow>(state, draft => {
    switch (action.type) {
      case Constants.GENERATE_ENVIRONMENT_REQUEST:
      case Constants.DESTROY_WORKFLOW:
        draft.generating = true;
        draft.generatingFailed = false;
        draft.fetching = true;
        draft.fetchingFailed = false;
        draft.state.uiData = initialState.environmentWorkflow.state.uiData;
        break;
      case Constants.GENERATE_SCALE_OUT_REQUEST:
        draft.generating = true;
        draft.generatingFailed = false;
        draft.fetching = true;
        draft.fetchingFailed = false;
        break;
      case Constants.GENERATE_ENVIRONMENT_REQUEST_SUCCESS:
        draft.state = action.payload.state;
        draft.steps.selectProduct = {
          fetching: false,
          failed: false,
          products: action.payload.products,
          binaryMappings: state.steps.selectProduct.binaryMappings,
          existingVcfDeployedProductIds:
            state.steps.selectProduct.existingVcfDeployedProductIds
        };
        draft.generating = false;
        break;
      case Constants.GENERATE_ENVIRONMENT_REQUEST_FAILED:
        draft.generating = false;
        draft.generatingFailed = true;
        break;
      case Constants.GENERATE_SCALE_OUT_REQUEST_FAILED:
        draft.generating = false;
        draft.generatingFailed = true;
        draft.errorStatus = action.payload.errorStatus;
        break;
      case Constants.INITIALIZE_WORKFLOW:
        draft.fetching = true;
        draft.fetchingFailed = false;
        draft.submitStatus.submitting = false;
        draft.submitStatus.failed = false;
        break;
      case Constants.INITIALIZE_WORKFLOW_SUCCESS:
        draft.fetching = false;
        draft.state = action.payload.state;
        draft.steps.environment.datacenters = action.payload.datacenters;
        break;
      case Constants.INITIALIZE_WORKFLOW_FAILED:
        draft.fetching = false;
        draft.fetchingFailed = true;
        break;
      case Constants.UPDATE_ENVIRONMENT_STEP_STATES:
        const uiData = toSafeUIData(state.state.uiData);
        uiData.stepStates = action.payload.stepStates;
        draft.state.uiData = JSON.stringify(uiData);
        break;
      case Constants.INITIALIZE_STEP_PRODUCTS_SUCCESS:
        draft.state.environmentRequestDTO.products = action.payload.products;
        break;
      case Constants.ADD_PRODUCT_NODE:
        const { nodeMetadata } = action.payload;
        let pIdx = draft.steps.products.productsMetadata.findIndex(
          p => p.productName === action.payload.productId
        );
        if (pIdx >= 0) {
          draft.state.environmentRequestDTO.products[pIdx].nodes.push({
            type: nodeMetadata.name,
            properties: nodeMetadata.properties.reduce((acc, cur) => {
              return {
                ...acc,
                [cur.name]: cur.defaultValue
              };
            }, {})
          });
        }
        break;
      case Constants.DELETE_PRODUCT_NODE:
        const { productId, nodeIndex } = action.payload;
        pIdx = draft.steps.products.productsMetadata.findIndex(
          p => p.productName === productId
        );
        if (pIdx >= 0) {
          draft.state.environmentRequestDTO.products[pIdx].nodes.splice(
            nodeIndex,
            1
          );
        }
        break;
      case Constants.PATCH_ENVIRONMENT_STATE:
      case Constants.SAVE_ENVIRONMENT_WORKFLOW:
      case Constants.SUBMIT_STEP_ENVIRONMENT:
      case Constants.SUBMIT_ENVIRONMENT_WORKFLOW:
        draft.submitStatus.submitting = true;
        draft.submitStatus.failed = false;
        break;
      case Constants.PATCH_ENVIRONMENT_STATE_SUCCESS:
      case Constants.SAVE_ENVIRONMENT_WORKFLOW_SUCCESS:
        draft.submitStatus.submitting = false;
        draft.state = action.payload.state;
        break;
      case Constants.SUBMIT_ENVIRONMENT_WORKFLOW_SUCCESS:
        draft.submitStatus.submitting = false;
        break;
      case Constants.SUBMIT_ENVIRONMENT_WORKFLOW_FAILED:
        draft.submitStatus.submitting = false;
        draft.submitStatus.failed = true;
        break;
      case Constants.PATCH_ENVIRONMENT_STATE_FAILED:
      case Constants.SAVE_ENVIRONMENT_WORKFLOW_FAILED:
        draft.submitStatus.submitting = false;
        draft.submitStatus.failed = true;
        draft.submitStatus.message = action.payload.message;
        break;
      case Constants.UPDATE_REQUEST_TYPE:
        draft.state.requestType = action.payload.requestType;
        break;
      case Constants.SET_REQUEST_ID:
        draft.state.requestId = action.payload.isVIDM
          ? 'globalenvironment'
          : generateUniqueId();
        const uiDataState = toSafeUIData(state.state.uiData);
        uiDataState.vidmToggled = true;
        draft.state.uiData = JSON.stringify(uiDataState);
        break;
      case Constants.RESET_STATUS:
        draft.generating = false;
        draft.generatingFailed = false;
        draft.errorStatus = 0;
        break;
      case Constants.GENERATE_ORGANIC_GROWTH_REQUEST:
        draft.generating = true;
        draft.generatingFailed = false;
        break;
      case Constants.GENERATE_ORGANIC_GROWTH_REQUEST_SUCCESS:
        draft.generating = false;
        draft.generatingFailed = false;
        break;
      case Constants.GENERATE_ORGANIC_GROWTH_REQUEST_FAILED:
        draft.generating = false;
        draft.generatingFailed = true;
      // no default
    }
  });
};

const stepsReducer = combineReducers({
  environment: environmentStep,
  selectProduct: selectProductStep,
  eula: eulaStep,
  infrastructure: infrastructureStep,
  binaryMapping: binaryMappingStep,
  network: networkStep,
  products: productsStep,
  precheck: precheckStep
});

export const environmentWorkflow = (
  state: EnvironmentWorkflow = initialState.environmentWorkflow,
  action: Action
): EnvironmentWorkflow => {
  const steps = stepsReducer(state.steps, action as any);
  const interimState: EnvironmentWorkflow = {
    ...state,
    steps
  };
  const finalState = stateReducer(interimState, action);
  return finalState;
};

export default environmentWorkflow;
